import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';

const OMOP_VOCABULARY_SEARCH_COPY = {
  de: {
    title: 'OMOP Vocabulary Suche',
    intro: 'Durchsuchen Sie die lokale OMOP Vocabulary Datenbank, um manuell ein passendes Konzept auszuwählen.',
    searchTerm: 'Suchbegriff',
    searchPlaceholder: 'z.B. Systolic blood pressure',
    vocabularyLabel: 'Vocabulary',
    domainLabel: 'Domain',
    all: 'Alle',
    searchLoading: 'Suche läuft...',
    search: 'Suchen',
    results: 'Suchergebnisse',
    standard: 'Standard',
    synonyms: 'Synonyme',
    useSelected: 'Ausgewähltes Konzept verwenden',
    help: 'Geben Sie einen Suchbegriff ein und klicken Sie auf "Suchen", um OMOP Konzepte zu finden.',
  },
  en: {
    title: 'OMOP vocabulary search',
    intro: 'Search the local OMOP vocabulary database to manually select a matching concept.',
    searchTerm: 'Search term',
    searchPlaceholder: 'e.g. Systolic blood pressure',
    vocabularyLabel: 'Vocabulary',
    domainLabel: 'Domain',
    all: 'All',
    searchLoading: 'Searching...',
    search: 'Search',
    results: 'Search results',
    standard: 'Standard',
    synonyms: 'Synonyms',
    useSelected: 'Use selected concept',
    help: 'Enter a search term and click "Search" to find OMOP concepts.',
  },
  pt: {
    title: 'Pesquisa no vocabulário OMOP',
    intro: 'Pesquise na base de dados local do vocabulário OMOP para selecionar manualmente um conceito adequado.',
    searchTerm: 'Termo de pesquisa',
    searchPlaceholder: 'ex.: Systolic blood pressure',
    vocabularyLabel: 'Vocabulário',
    domainLabel: 'Domínio',
    all: 'Todos',
    searchLoading: 'A pesquisar...',
    search: 'Pesquisar',
    results: 'Resultados da pesquisa',
    standard: 'Padrão',
    synonyms: 'Sinónimos',
    useSelected: 'Usar conceito selecionado',
    help: 'Introduza um termo de pesquisa e clique em "Pesquisar" para encontrar conceitos OMOP.',
  },
};

/**
 * OmopVocabularySearch Component
 * Manual OMOP vocabulary search interface
 * Allows users to search local vocabulary database and select concepts manually
 *
 * @param {object} props
 * @param {function} props.onSelectConcept - Callback when user selects a concept
 * @param {string} props.parameterName - Current parameter name for context
 * @param {boolean} props.isSearching - Whether search is in progress
 */
const OmopVocabularySearch = ({
  onSelectConcept,
  parameterName,
  isSearching = false
}) => {
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [vocabularyFilter, setVocabularyFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_VOCABULARY_SEARCH_COPY[language];

  const resultsPerPage = 10;

  // Mock search function - in production, this would call the API
  const handleSearch = () => {
    // TODO: Implement actual search via mockMappingService
    // For now, just show placeholder
    console.log('Searching for:', searchQuery, 'vocab:', vocabularyFilter, 'domain:', domainFilter);
  };

  const handleSelectConcept = (concept) => {
    setSelectedConcept(concept);
  };

  const handleConfirmSelection = () => {
    if (selectedConcept && onSelectConcept) {
      onSelectConcept(selectedConcept);
    }
  };

  useEffect(() => {
    // Initialize with parameter name as default search query
    if (parameterName) {
      setSearchQuery(parameterName);
    }
  }, [parameterName]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {copy.title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {copy.intro}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Search Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={copy.searchTerm}
            placeholder={copy.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>{copy.vocabularyLabel}</InputLabel>
            <Select
              value={vocabularyFilter}
              label={copy.vocabularyLabel}
              onChange={(e) => setVocabularyFilter(e.target.value)}
            >
              <MenuItem value="all">{copy.all}</MenuItem>
              <MenuItem value="LOINC">LOINC</MenuItem>
              <MenuItem value="SNOMED">SNOMED</MenuItem>
              <MenuItem value="RxNorm">RxNorm</MenuItem>
              <MenuItem value="ICD10">ICD10</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>{copy.domainLabel}</InputLabel>
            <Select
              value={domainFilter}
              label={copy.domainLabel}
              onChange={(e) => setDomainFilter(e.target.value)}
            >
              <MenuItem value="all">{copy.all}</MenuItem>
              <MenuItem value="Measurement">Measurement</MenuItem>
              <MenuItem value="Observation">Observation</MenuItem>
              <MenuItem value="Condition">Condition</MenuItem>
              <MenuItem value="Procedure">Procedure</MenuItem>
              <MenuItem value="Drug">Drug</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Button
        variant="contained"
        startIcon={isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
        onClick={handleSearch}
        disabled={!searchQuery.trim() || isSearching}
        fullWidth
        sx={{ mb: 3 }}
      >
        {isSearching ? copy.searchLoading : copy.search}
      </Button>

      {/* Search Results */}
      {searchResults.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {copy.results} ({searchResults.length})
          </Typography>
          <List sx={{ mb: 2 }}>
            {searchResults.map((concept, idx) => (
              <ListItem key={idx} sx={{ p: 0, mb: 1 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    width: '100%',
                    cursor: 'pointer',
                    border: selectedConcept?.conceptId === concept.conceptId
                      ? '2px solid #1976d2'
                      : '1px solid #e0e0e0',
                    backgroundColor: selectedConcept?.conceptId === concept.conceptId
                      ? '#e3f2fd'
                      : 'white',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={() => handleSelectConcept(concept)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {concept.conceptName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {concept.conceptId} • Code: {concept.conceptCode}
                      </Typography>
                    </Box>
                    {selectedConcept?.conceptId === concept.conceptId && (
                      <CheckIcon color="primary" />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={concept.vocabulary} size="small" variant="outlined" />
                    <Chip label={concept.domain} size="small" variant="outlined" />
                    {concept.standardConcept === 'S' && (
                      <Chip label={copy.standard} size="small" color="success" />
                    )}
                  </Box>
                  {concept.conceptSynonyms && concept.conceptSynonyms.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {copy.synonyms}: {concept.conceptSynonyms.slice(0, 3).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<LinkIcon />}
                      href={`https://athena.ohdsi.org/search-terms/terms/${concept.conceptId}`}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ATHENA
                    </Button>
                  </Box>
                </Paper>
              </ListItem>
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}

          {/* Confirm Selection */}
          {selectedConcept && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleConfirmSelection}
              fullWidth
            >
              {copy.useSelected}
            </Button>
          )}
        </Box>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">
            <InfoIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            {copy.help}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default OmopVocabularySearch;
